import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { responseHandler } from "@msuite/ui/utils";
import {
	type PostCalculationMemberSchema,
	postCalculationMemberSchema,
} from "@msuite/utils/schemas";
import type { CalculationMemberListItem, Nullable } from "@msuite/utils/types";
import { FormProvider, useForm } from "react-hook-form";
import { CalculationMemberForm } from "@/forms/calculation-member-form";
import { client } from "@/hono";

/** Props Interface */
interface AddCalculationMemberModalProps extends ModalProps {
	calculationId: string;
	excludeUserIds: string[];
	onComplete: (member: Nullable<CalculationMemberListItem>) => Promise<void> | void;
}

export function AddCalculationMemberModal({
	isOpen,
	onClose,
	calculationId,
	excludeUserIds,
	onComplete,
}: AddCalculationMemberModalProps) {
	/** Hooks */
	const { t } = useTranslation();
	const methods = useForm<PostCalculationMemberSchema>({
		resolver: resolver(postCalculationMemberSchema),
	});

	/** Functions */
	async function handleOnSubmit(json: PostCalculationMemberSchema) {
		try {
			const response = await client.calculations[":calculationId"].members.$post({
				param: { calculationId },
				json,
			});
			const data = await responseHandler(response);
			await onComplete(data.member);
			handleOnClose();
		} catch (error) {
			console.error(error);
		}
	}

	function handleOnClose() {
		onClose();
		methods.reset();
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				isOpen={isOpen}
				onClose={handleOnClose}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
				title={t("calculations.add_member")}
				data-cy="add-calculation-member-form"
			>
				<Modal.Body>
					<CalculationMemberForm excludeUserIds={excludeUserIds} />
				</Modal.Body>
				<Modal.FooterForm />
			</Modal>
		</FormProvider>
	);
}
